<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { resolveImageAlt } from "@/utils/imageAlt";

const props = defineProps<{
  trekTitle: string;
}>();

const page = usePage().value;

const articleAlt = (
  article: {
    title?: string;
    blogImage?: { alt?: string };
    blogImage1?: { alt?: string };
  },
  which: "blogImage" | "blogImage1" = "blogImage",
) => resolveImageAlt(article?.[which]?.alt, article?.title);

const parseDate = (dateString: string) => {
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const normalizeString = (str: string) => {
  return str.replace(/'/g, ""); // Replace all apostrophes with an empty string
};

const articles = computed(() => {
  const excludedWords = [
    "treks",
    "tour",
    "trail",
    "route",
    "the",
    "and",
    "of",
    "region",
  ];
  const trekWords = normalizeString(props.trekTitle)
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((word) => word.length > 2 && !excludedWords.includes(word));

  const combinedArticles = [
    ...(page?.blogArticles || []),
    ...(page?.newsArticles || []),
  ];

  return combinedArticles
    .filter((article) => {
      const normalizedTitle = normalizeString(article.title).toLowerCase();

      const tagString =
        typeof article.tags?.value === "string" ? article.tags.value : "";
      const tagList = tagString
        .split(",")
        .map((tag) => normalizeString(tag).toLowerCase().trim())
        .filter(Boolean);

      // Count how many trekWords appear in title or tags
      let matchCount = 0;

      trekWords.forEach((word) => {
        const inTitle = normalizedTitle.includes(word);
        const inTags = tagList.some(
          (tag) => tag.includes(word) || word.includes(tag),
        );
        if (inTitle || inTags) {
          matchCount++;
        }
      });

      // ✅ Only return the article if at least 1 strong match
      return matchCount > 0;
    })
    .sort(
      (a, b) =>
        parseDate(b.published).getTime() - parseDate(a.published).getTime(),
    );
});

const itemsToShow = ref(4);
const windowWidth = ref(0); // default value
const isMobile = ref(false);

function updateWidth() {
  if (process.client) {
    windowWidth.value = window.innerWidth;

    if (isMobile.value) {
      itemsToShow.value = 1;
    } else {
      itemsToShow.value = 3;
    }
  }
}

onMounted(() => {
  isMobile.value = window.innerWidth <= 991;
  // Access the window object only after the component is mounted
  windowWidth.value = window.innerWidth;
  updateWidth();
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});
</script>

<template>
  <section v-if="articles.length" id="featuredArticles">
    <div class="container">
      <h2>Experience the Mountains with Us</h2>
      <h3>Read our latest articles</h3>
      <section class="articles-wrap">
        <div
          v-for="(article, index) in articles.slice(0, 1)"
          :key="index"
          class="article"
        >
          <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
            <div class="article-image">
              <NuxtPicture
                v-if="article?.blogImage1"
                format="webp"
                loading="lazy"
                :src="article?.blogImage1.url"
                :width="article?.blogImage1.width"
                :height="article?.blogImage1.height"
                :alt="articleAlt(article, 'blogImage1')"
                sizes="sm:100vw md:50vw lg:600px"
              />
            </div>
            <div class="article-meta">
              <h3>{{ article?.title }}</h3>
              <p class="date">
                {{ article?.published }}
              </p>
            </div>
          </NuxtLink>
        </div>
        <div
          v-for="(article, index) in articles.slice(1, 3)"
          :key="index"
          class="article"
        >
          <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
            <div class="article-image">
              <NuxtPicture
                v-if="article?.blogImage"
                format="webp"
                loading="lazy"
                :src="article?.blogImage.url"
                :width="article?.blogImage.width"
                :height="article?.blogImage.height"
                :alt="articleAlt(article, 'blogImage')"
                sizes="sm:100vw md:50vw lg:600px"
              />
            </div>
            <div class="article-meta">
              <h3>{{ article?.title }}</h3>
              <p class="date">{{ article.published }}</p>
            </div>
          </NuxtLink>
        </div>
      </section>
    </div>
  </section>
</template>
